// valuePass/admin/src/App.tsx
//
// Root router for the ValuePass admin dashboard.
//
// ── Route structure ───────────────────────────────────────────────────────────
//
//   /login                    LoginPage        (no auth required)
//   /                         AppShell
//   ├─ /                      DashboardPage    (index)
//   ├─ /campaigns             CampaignsPage
//   ├─ /campaigns/:id         CampaignDetailPage
//   ├─ /vouchers              VouchersPage
//   ├─ /beneficiaries         BeneficiariesPage
//   ├─ /analytics             AnalyticsPage
//   ├─ /merchants             MerchantsPage    (stub)
//   ├─ /settings              SettingsPage     (stub)
//   └─ *                      NotFoundPage
//
// ── Code-splitting ────────────────────────────────────────────────────────────
//
//   All pages are React.lazy-loaded so the initial bundle only contains the
//   AppShell, routing logic, and shared UI primitives. Each page chunk is
//   fetched on first navigation to that route.
//
//   A lightweight <PageSuspense> wrapper renders a full-page spinner while a
//   chunk is being fetched. The spinner matches the brand colours so there is
//   no flash of unstyled content.
//
// ── Auth protection ───────────────────────────────────────────────────────────
//
//   AppShell (layout/AppShell.tsx) contains the Firebase Auth onAuthStateChanged
//   listener. It redirects unauthenticated users to /login and renders a full-
//   page loader while the auth state is being resolved. Routes nested inside
//   AppShell therefore automatically require authentication.
//
//   The /login route renders LoginPage outside AppShell so it is always
//   accessible without authentication.
//
// ── QueryClient ───────────────────────────────────────────────────────────────
//
//   TanStack Query's QueryClient is created here (single instance per app
//   lifetime) and provided via QueryClientProvider wrapping the entire router.
//   ReactQueryDevtools are included in development builds only (tree-shaken
//   in production by Vite's import.meta.env.DEV check).
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { Suspense, lazy, type ReactNode } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AppShell } from "@/components/layout/AppShell";

// ── QueryClient ───────────────────────────────────────────────────────────────
//
// Global defaults chosen for a live-data admin dashboard:
//
//   staleTime: 30 s
//     Analytics data is updated via real-time Firestore listeners, so REST
//     query cache only needs to stay fresh for ~30 s before a background
//     refetch is triggered.
//
//   retry: 1
//     Fail fast on permission errors (e.g., 401 / 403) rather than retrying
//     3 times (the library default).
//
//   refetchOnWindowFocus: true
//     Re-validates query data when the user returns to the tab, catching
//     changes made in another tab or by another admin.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:            30_000,
      retry:                1,
      refetchOnWindowFocus: true,
      // Throw errors to the nearest ErrorBoundary instead of swallowing them.
      // Individual pages can override with throwOnError: false.
      throwOnError:         false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ── Lazy page imports ─────────────────────────────────────────────────────────
//
// Each page is a separate Rollup chunk (see vite.config.ts manualChunks).
// The dynamic import path must be a string literal for Vite's static analyser.

const DashboardPage      = lazy(() => import("@/pages/DashboardPage"));
const BeneficiariesPage  = lazy(() => import("@/pages/BeneficiariesPage"));
const VouchersPage       = lazy(() => import("@/pages/VouchersPage"));
const LoginPage          = lazy(() => import("@/pages/LoginPage"));
const CampaignsPage      = lazy(() => import("@/pages/CampaignsPage"));
const CampaignDetailPage = lazy(() => import("@/pages/CampaignDetailPage"));
const AnalyticsPage      = lazy(() => import("@/pages/AnalyticsPage"));

// ── Stub pages ────────────────────────────────────────────────────────────────
//
// Merchants and Settings pages are not yet implemented. These stubs render a
// "Coming soon" placeholder so that nav links point to valid routes.

const StubPage: React.FC<{ title: string; icon: string }> = ({ title, icon }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
    <span className="text-6xl select-none">{icon}</span>
    <h2 className="text-xl font-semibold text-gray-700">{title}</h2>
    <p className="text-sm text-gray-400">This page is coming soon.</p>
  </div>
);

const MerchantsPage: React.FC = () => (
  <StubPage title="Merchants" icon="🏪" />
);

const SettingsPage: React.FC = () => (
  <StubPage title="Settings" icon="⚙️" />
);

// ── PageSuspense ──────────────────────────────────────────────────────────────
//
// Shown while a lazy page chunk is being fetched over the network.
// Matches the brand green spinner used in AppShell's full-page loader so
// there is no jarring style change between auth loading and page loading.

function PageFallback() {
  return (
    <div
      className="flex items-center justify-center py-32"
      aria-label="Loading page"
      role="status"
    >
      <div className="w-8 h-8 rounded-full border-4 border-brand-green border-t-transparent animate-spin" />
    </div>
  );
}

function PageSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

// ── Not found ─────────────────────────────────────────────────────────────────

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <span className="text-6xl select-none">🔍</span>
      <h2 className="text-xl font-semibold text-gray-700">Page not found</h2>
      <p className="text-sm text-gray-400">
        The page you are looking for does not exist.
      </p>
      <a
        href="/"
        className="mt-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-lg hover:bg-brand-green-dark transition-colors"
      >
        Go to Dashboard
      </a>
    </div>
  );
}

// ── Router definition ─────────────────────────────────────────────────────────
//
// createBrowserRouter (v6.4+) is used instead of <BrowserRouter> so that
// future data-router features (loaders, actions, errorElement) can be adopted
// incrementally.

const router = createBrowserRouter([
  // ── Public routes (no auth required) ─────────────────────────────────────
  {
    path:    "/login",
    element: (
      <PageSuspense>
        <LoginPage />
      </PageSuspense>
    ),
  },

  // ── Protected routes (auth required — enforced by AppShell) ──────────────
  {
    path:    "/",
    element: <AppShell />,
    children: [
      // Dashboard (default landing page)
      {
        index:   true,
        element: (
          <PageSuspense>
            <DashboardPage />
          </PageSuspense>
        ),
      },

      // Campaigns list
      {
        path:    "campaigns",
        element: (
          <PageSuspense>
            <CampaignsPage />
          </PageSuspense>
        ),
      },

      // Campaign detail (shows full analytics + rules + beneficiaries for one campaign)
      {
        path:    "campaigns/:campaignId",
        element: (
          <PageSuspense>
            <CampaignDetailPage />
          </PageSuspense>
        ),
      },

      // Voucher ledger
      {
        path:    "vouchers",
        element: (
          <PageSuspense>
            <VouchersPage />
          </PageSuspense>
        ),
      },

      // Beneficiary management
      {
        path:    "beneficiaries",
        element: (
          <PageSuspense>
            <BeneficiariesPage />
          </PageSuspense>
        ),
      },

      // Analytics deep-dive (campaign trend charts, per-merchant breakdown)
      {
        path:    "analytics",
        element: (
          <PageSuspense>
            <AnalyticsPage />
          </PageSuspense>
        ),
      },

      // Merchant registry (stub)
      {
        path:    "merchants",
        element: (
          <PageSuspense>
            <MerchantsPage />
          </PageSuspense>
        ),
      },

      // Settings (stub)
      {
        path:    "settings",
        element: (
          <PageSuspense>
            <SettingsPage />
          </PageSuspense>
        ),
      },

      // Catch-all 404 inside the shell (keeps the sidebar visible)
      {
        path:    "*",
        element: <NotFoundPage />,
      },
    ],
  },

  // ── Root-level catch-all ──────────────────────────────────────────────────
  //
  // Handles any path that is not /login and not under / (e.g., malformed deep
  // links). Redirects to / so AppShell + DashboardPage handle the auth check.
  {
    path:    "*",
    element: <Navigate to="/" replace />,
  },
]);

// ── App ───────────────────────────────────────────────────────────────────────

/**
 * Root application component.
 *
 * Wraps the entire app in:
 *   1. QueryClientProvider  — makes the queryClient available to all hooks
 *   2. RouterProvider       — mounts the browser router
 *   3. ReactQueryDevtools   — rendered in development builds only
 *
 * There is intentionally no React.StrictMode wrapper here because
 * Firebase's onAuthStateChanged + Firestore onSnapshot listeners trigger
 * false-positive "double invocation" warnings in strict mode that can
 * confuse debugging. Strict mode can be re-enabled once Firebase SDK v11+
 * fully supports the concurrent-safe patterns it requires.
 */
const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
    {import.meta.env.DEV && (
      <ReactQueryDevtools
        initialIsOpen={false}
        buttonPosition="bottom-right"
      />
    )}
  </QueryClientProvider>
);

export default App;
